import { useEffect, useRef, useState, useCallback } from 'react';

interface PerformanceMetrics {
  fps: number;
  memoryUsage?: number;
  renderTime: number;
  isLowPerformance: boolean;
}

interface PerformanceMonitorOptions {
  enableFPSMonitoring?: boolean;
  enableMemoryMonitoring?: boolean;
  fpsThreshold?: number;
  memoryThreshold?: number;
  onPerformanceChange?: (metrics: PerformanceMetrics) => void;
}

export const usePerformanceMonitor = (options: PerformanceMonitorOptions = {}) => {
  const {
    enableFPSMonitoring = true,
    enableMemoryMonitoring = true,
    fpsThreshold = 30,
    memoryThreshold = 50, // MB
    onPerformanceChange
  } = options;

  const [metrics, setMetrics] = useState<PerformanceMetrics>({
    fps: 60,
    memoryUsage: 0,
    renderTime: 0,
    isLowPerformance: false
  });

  const frameCount = useRef(0);
  const lastTime = useRef(performance.now());
  const fpsHistory = useRef<number[]>([]);
  const animationFrame = useRef<number>();

  // FPS monitoring
  const measureFPS = useCallback(() => {
    const now = performance.now();
    frameCount.current++;

    if (now - lastTime.current >= 1000) {
      const fps = Math.round((frameCount.current * 1000) / (now - lastTime.current));
      
      // Keep FPS history for smoothing
      fpsHistory.current.push(fps);
      if (fpsHistory.current.length > 10) {
        fpsHistory.current.shift();
      }

      const averageFPS = fpsHistory.current.reduce((a, b) => a + b, 0) / fpsHistory.current.length;
      
      setMetrics(prev => ({
        ...prev,
        fps: Math.round(averageFPS),
        isLowPerformance: averageFPS < fpsThreshold
      }));

      frameCount.current = 0;
      lastTime.current = now;
    }

    if (enableFPSMonitoring) {
      animationFrame.current = requestAnimationFrame(measureFPS);
    }
  }, [enableFPSMonitoring, fpsThreshold]);

  // Memory monitoring
  const measureMemory = useCallback(() => {
    if (enableMemoryMonitoring && 'memory' in performance) {
      const memory = (performance as any).memory;
      const usedMemory = memory.usedJSHeapSize / (1024 * 1024); // Convert to MB
      
      setMetrics(prev => ({
        ...prev,
        memoryUsage: Math.round(usedMemory),
        isLowPerformance: prev.isLowPerformance || usedMemory > memoryThreshold
      }));
    }
  }, [enableMemoryMonitoring, memoryThreshold]);

  // Render time monitoring
  const measureRenderTime = useCallback((startTime: number) => {
    const renderTime = performance.now() - startTime;
    setMetrics(prev => ({
      ...prev,
      renderTime: Math.round(renderTime * 100) / 100
    }));
  }, []);

  // Start monitoring
  useEffect(() => {
    if (enableFPSMonitoring) {
      animationFrame.current = requestAnimationFrame(measureFPS);
    }

    const memoryInterval = enableMemoryMonitoring 
      ? setInterval(measureMemory, 5000) // Check memory every 5 seconds
      : null;

    return () => {
      if (animationFrame.current) {
        cancelAnimationFrame(animationFrame.current);
      }
      if (memoryInterval) {
        clearInterval(memoryInterval);
      }
    };
  }, [enableFPSMonitoring, enableMemoryMonitoring, measureFPS, measureMemory]);

  // Notify on performance changes
  useEffect(() => {
    if (onPerformanceChange) {
      onPerformanceChange(metrics);
    }
  }, [metrics, onPerformanceChange]);

  // Performance optimization suggestions
  const getOptimizationSuggestions = useCallback(() => {
    const suggestions: string[] = [];

    if (metrics.fps < 30) {
      suggestions.push('Consider reducing animation complexity or particle count');
    }

    if (metrics.memoryUsage && metrics.memoryUsage > 50) {
      suggestions.push('High memory usage detected - consider optimizing data structures');
    }

    if (metrics.renderTime > 16.67) { // 60fps = 16.67ms per frame
      suggestions.push('Render time is high - consider optimizing component rendering');
    }

    return suggestions;
  }, [metrics]);

  // Device capability detection
  const getDeviceCapabilities = useCallback(() => {
    const hardwareConcurrency = navigator.hardwareConcurrency || 2;
    const deviceMemory = (navigator as any).deviceMemory || 4;
    const connection = (navigator as any).connection;
    
    return {
      cores: hardwareConcurrency,
      memory: deviceMemory,
      connectionType: connection?.effectiveType || 'unknown',
      isLowEnd: hardwareConcurrency <= 2 || deviceMemory <= 2,
      isMobile: /Android|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent)
    };
  }, []);

  return {
    metrics,
    measureRenderTime,
    getOptimizationSuggestions,
    getDeviceCapabilities,
    isMonitoring: enableFPSMonitoring || enableMemoryMonitoring
  };
};

// Hook for component-level performance monitoring
export const useComponentPerformance = (componentName: string) => {
  const renderStartTime = useRef<number>();
  const renderCount = useRef(0);

  useEffect(() => {
    renderStartTime.current = performance.now();
    renderCount.current++;
  });

  useEffect(() => {
    if (renderStartTime.current) {
      const renderTime = performance.now() - renderStartTime.current;
      
      // Log slow renders in development
      if (process.env.NODE_ENV === 'development' && renderTime > 16) {
        console.warn(`Slow render detected in ${componentName}: ${renderTime.toFixed(2)}ms (render #${renderCount.current})`);
      }
    }
  });

  return {
    renderCount: renderCount.current,
    markRenderStart: () => {
      renderStartTime.current = performance.now();
    },
    markRenderEnd: () => {
      if (renderStartTime.current) {
        return performance.now() - renderStartTime.current;
      }
      return 0;
    }
  };
};